import { EditOutlined, EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { GeneralDrawer } from "@components/general-drawer";
import useEnforceLoggedIn from "@hooks/useEnforceLoggedIn";
import { NotificationType, showNotification } from "@lib/notification";
import { usePartnerPortalPasswordUpdateMutation } from "@ppServices/Partners";
import { Button, ButtonProps, Col, Collapse, Form, Input, Row } from "antd";
import CollapsePanel from "antd/lib/collapse/CollapsePanel";
import { useEffect, useState } from "react";


interface ProfileButtonProps extends ButtonProps {
    text?: string;
    pointOfContactInfo?: any;
    isLoading?: boolean;
}


const PointOfInterestFormFields = ({ className = '' }) => {

    return (
        <>

            <Form.Item
                label="First Name"
                name="firstName"
                rules={[{
                    required: true,
                    message: 'Please input your First Name!'
                }]}>

                <Input type="text" />

            </Form.Item>

            <Form.Item

                label="Last Name"
                name="lastName"
                rules={[{
                    required: true,
                    message: 'Please input your Last Name!'
                }]}>

                <Input type="text" />

            </Form.Item>

            <Form.Item
                label="Email"
                name="email"
                rules={[{
                    type: 'email',
                    required: true,
                    message: 'Please input your Email!'
                }]}>

                <Input type="email" />

            </Form.Item>

            <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[{
                    required: true,
                    message: 'Please input your phone number!'
                }]}>

                <Input type="tel" />

            </Form.Item>

        </>
    )
}

const PasswordFormFields = ({ className = '' }) => {

    return (
        <>

            <Form.Item
                label="Password"
                name="password"
                rules={[{
                    required: true,
                    message: 'Please input your password!'
                }]}
            >

                <Input.Password
                    type="password"
                    iconRender={(visible) => (visible
                        ? <EyeOutlined />
                        : <EyeInvisibleOutlined />
                    )}
                />

            </Form.Item>

            <Form.Item
                label="Confirm Password"
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                    {
                        required: true,
                        message: 'Please confirm your password!',
                    },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('password') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Doesnt Match!'));
                        },
                    }),
                ]}
            >

                <Input.Password
                    type="confirm_password"
                    iconRender={(visible) => (visible
                        ? <EyeOutlined />
                        : <EyeInvisibleOutlined />
                    )}
                />

            </Form.Item>

        </>
    );
}

const AccountManagmentForms = ({
    className = '',
    form,
    onFinish,
    pointOfContactInfo
}) => {

    return (
        <Form
            className={className}
            form={form}
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            layout="horizontal"
            onFinish={onFinish}
        >
            <Collapse
                defaultActiveKey={['1', '2']}
                expandIconPosition={"start"}
                ghost
            >
                <CollapsePanel
                    header={`Manage Password`}
                    collapsible={`icon`}
                    key={"1"}
                >

                    <PasswordFormFields />

                </CollapsePanel>

                <CollapsePanel
                    header={`Point of Contact`}
                    collapsible={`icon`}
                    key={"2"}
                >

                    {pointOfContactInfo ? 
                        <>
                            <ul className={`info`}>
                                <li><strong>First Name:</strong> {pointOfContactInfo.firstName}</li>
                                <li><strong>Last Name:</strong> {pointOfContactInfo.lastName}</li>
                                <li><strong>Email:</strong>&nbsp;
                                    <a href={`mailTo:${pointOfContactInfo.email}`}>
                                        {pointOfContactInfo.email}
                                    </a>
                                </li>
                            </ul>
                            <p><em>If you would like to edit this information, please contact your Covrprice Representative.</em></p>
                        </> : 
                            <p><em>Sorry, there was an error loading your point of contact person info. Please contact <a href={`mailTo:help@covrprice.com`}></a> to let us know, thanks.</em></p>
                        }

                </CollapsePanel>

            </Collapse>

        </Form >
    );
}

const EditProfileButton: React.FC<ProfileButtonProps> = ({
    type = 'link',
    isLoading = false,
    size = 'small',
    pointOfContactInfo,
    className = ''
}) => {

    const { user } = useEnforceLoggedIn();

    const [form] = Form.useForm();

    const [showDrawer, setShowDrawer] = useState<boolean>(false);

    const onClose = () => {
        setShowDrawer(false);
    };

    const [setNewPassword, newPasswordResults] = usePartnerPortalPasswordUpdateMutation();

    useEffect(() => {

        if (newPasswordResults.isSuccess) {
            let arg = newPasswordResults.originalArgs;
            let message = arg.active ? 'Field Enabled' : 'Field Disabled';
            showNotification(message, NotificationType.SUCCESS);
        }

        if (newPasswordResults.isError) {
            let arg = newPasswordResults.originalArgs;
            let message = 'Error: Updating your password. Please contact your service representative.';
            showNotification(message, NotificationType.ERROR);
        }

    }, [newPasswordResults]);

    const handleFinish = async (values) => {
        let { confirmPassword, password } = values;

        if (password === confirmPassword &&
            confirmPassword !== undefined &&
            password !== undefined
        ) {
            await setNewPassword({
                userId: user.id,
                newPassword: password,
            });
        }

    };

    const handleSubmitForm = async () => {
        form.submit();
    };

    return (
        <>
            <Button
                onClick={() => setShowDrawer(true)}
                className={className}
                type={type}
                size={size}
            >
                <EditOutlined
                    style={{ color: `black` }}
                    className="font-12"
                />

            </Button>

            <GeneralDrawer
                title={`Account Management`}
                visible={showDrawer}
                onClose={onClose}
                renderFooter={
                    <Row justify="center">
                        <Col >
                            <Button
                                loading={isLoading}
                                className="outlineButton thickButton width-250px"
                                onClick={handleSubmitForm}
                            >
                                Save
                            </Button>
                        </Col>
                    </Row>
                }
            >
                <Row className={`pp-edit-profile-drawer`}>

                    <Col span={`24`}>

                        <AccountManagmentForms
                            form={form}
                            onFinish={handleFinish}
                            pointOfContactInfo={pointOfContactInfo}
                        />

                    </Col>

                </Row>

            </GeneralDrawer>
        </>
    )
};

export default EditProfileButton;

